/**
 * Store Pinia des lignes de commande client (Composition API)
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { IClientOrderRow } from '../models/clientorderrow';
import { IClientOrderRowApiResponse } from '../models/api/clientOrderRowApiResponse';
import { IClientOrderRowFilter } from '../models/filters/clientOrderRowFilter';
import ClientOrderRowServices from '../services/ClientOrderRowService';
import { IProduction } from '../models/production';
import signalRManager from '../helper/signalRManager';
import IPayloadStore from '../models/api/payloadStore';
import { useClientOrderStore } from './clientOrder.store';
import { useParameterStore } from './parameter.store';
import { useBoars } from './boars.store';

/**
 * Store Pinia des lignes de commande client utilisant la Composition API
 */
const useClientOrderRowStore = defineStore('ClientOrderRows', () => {
    // State
    const datas = ref<IClientOrderRow[]>([]);
    const current = ref<IClientOrderRow>({} as IClientOrderRow);
    const currentProduction = ref<IProduction>({} as IProduction);

    // Actions - Hydration
    /**
     * Hydrate ClientOrderRows from API response
     */
    async function hydrateClientOrderRows(rows: IClientOrderRowApiResponse[]): Promise<IClientOrderRow[]> {
        if (rows.length === 0) return [];

        // 1. Get stores
        const clientOrderStore = useClientOrderStore();
        const parameterStore = useParameterStore();

        // 2. Load in parallel if needed
        const loadPromises: Promise<unknown>[] = [];
        if (clientOrderStore.datas.length === 0) {
            loadPromises.push(clientOrderStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const clientOrderMap = new Map(clientOrderStore.datas.map(o => [o.id, o]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: IClientOrderRow[] = [];
        for (const row of rows) {
            const model = { ...row } as unknown as IClientOrderRow;

            // Link references (O(1) via Map)
            model.clientOrder = clientOrderMap.get(row.clientOrderId) ?? model.clientOrder;
            model.conditionnement = parameterMap.get(row.conditionnementId) ?? model.conditionnement;

            models.push(model);
        }

        return models;
    }

    // Getters
    const getClientOrderRowById = computed(() => (id: number): IClientOrderRow | undefined => {
        return datas.value.find((item) => item.id === id);
    });

    /**
     * Récupération commandes pour la production en cours.
     * Inclut les commandes générales (articleCode == schéma) et les commandes spécifiques
     * dont le verrat demandé (particularityBoar) appartient au même schéma que la production.
     */
    const getOrderForProduction = computed(() => (): IClientOrderRow[] | undefined => {
        const schemaCode = currentProduction.value.animal?.geneticType?.code3
        const boarsStore = useBoars()
        return datas.value?.filter((clientOrderRow) => {
            const articleCode = clientOrderRow.articleCode || ''
            if (articleCode === schemaCode) return true
            // Commande spécifique : cherche le verrat par nom, sinon extrait les 3 premiers caractères
            const boar = boarsStore.datas.find(b => b.name === articleCode)
            const resolvedSchema = boar?.geneticType?.code3 ?? articleCode.substring(0, 3)
            return resolvedSchema === schemaCode
        })
    });

    /**
     * Récupération commandes hétérospermie pour un code schéma
     */
    const getOrderHeteroForCodeSchema = computed(() => (codeSchema: string): IClientOrderRow[] | undefined => {
        return datas.value?.filter((clientOrderRow) =>
            clientOrderRow.articleCode == codeSchema &&
            clientOrderRow.heterospermie == true &&
            clientOrderRow.clientOrder?.active == true
        );
    });

    // Actions
    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IClientOrderRowFilter) {
        const result = await ClientOrderRowServices.getAll<IClientOrderRowFilter, IClientOrderRowApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateClientOrderRows(result.data as IClientOrderRowApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id L'identifiant de l'élément à récupérer
     */
    async function get(id: number) {
        const result = await ClientOrderRowServices.getById<IClientOrderRowApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateClientOrderRows([result.data as IClientOrderRowApiResponse]);

            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }

            current.value = modelData;
        }
        return result;
    }

    /**
     * Handle SignalR updates for ClientOrderRow
     */
    async function handleSignalRUpdate(payload: IPayloadStore) {
        console.log("From ClientOrderRowStore : MajClientOrderRow", payload);
        switch (payload.action) {
            case "Create": {
                const index = datas.value.findIndex((a) => a.id == payload.data?.id);
                if (index == -1) {
                    // S'assurer que la commande parente existe dans le store
                    // (MajClientOrderRow peut arriver avant MajClientOrder)
                    const clientOrderStore = useClientOrderStore();
                    const rowData = payload.data as IClientOrderRowApiResponse;
                    if (rowData.clientOrderId && !clientOrderStore.datas.find(o => o.id === rowData.clientOrderId)) {
                        await clientOrderStore.get(rowData.clientOrderId);
                    }
                    // Hydrater avant d'ajouter (le payload SignalR contient des IDs bruts,
                    // pas les objets clientOrder/conditionnement nécessaires aux computed)
                    const [hydrated] = await hydrateClientOrderRows([rowData]);
                    datas.value.push(hydrated);
                }
                break;
            }
            case "Update": {
                const updateIndex = datas.value.findIndex((a) => a.id == payload.data?.id);
                if (updateIndex > -1) {
                    const [hydrated] = await hydrateClientOrderRows([payload.data as IClientOrderRowApiResponse]);
                    datas.value[updateIndex] = hydrated;
                }
                break;
            }
            case "Delete": {
                const deleteIndex = datas.value.findIndex((a) => a.id == payload.id);
                if (deleteIndex > -1)
                    datas.value.splice(deleteIndex, 1);
                break;
            }
        }
    }

    return {
        // State
        datas,
        current,
        currentProduction,
        // Getters
        getClientOrderRowById,
        getOrderForProduction,
        getOrderHeteroForCodeSchema,
        // Actions
        load,
        get,
        handleSignalRUpdate
    };
});

// SignalR handler registration
signalRManager.connection.on("MajClientOrderRow", (payload: IPayloadStore) => {
    useClientOrderRowStore().handleSignalRUpdate(payload);
});

export { useClientOrderRowStore };
